import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import VouchModal from './VouchModal';
import ViewClaimersModal from './ViewClaimersModal';
import axios from 'axios';

const CodeRequestCard = ({ codeRequest, onUpdate }) => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showVouchModal, setShowVouchModal] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimedCode, setClaimedCode] = useState(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showClaimersModal, setShowClaimersModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [markingAsDone, setMarkingAsDone] = useState(false);
  const [editFormData, setEditFormData] = useState({
    description: codeRequest.description || '',
    gameUrl: codeRequest.gameUrl || '',
    status: codeRequest.status || 'active',
    emailNotificationsEnabled: codeRequest.emailNotificationsEnabled !== undefined ? codeRequest.emailNotificationsEnabled : true
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isOwner = isAuthenticated && user && String(codeRequest.user._id) === String(user._id);
  const isRequest = codeRequest.type === 'request';
  const isOffer = codeRequest.type === 'offer';
  const hasCodesAvailable = isOffer && codeRequest.claimedCodes < codeRequest.totalCodes;

  const getPlatformIcon = (platform) => {
    const icons = {
      steam: '🎮',
      epic: '🏪',
      gog: '🕹️',
      xbox: '🎯',
      playstation: '🎲',
      nintendo: '🎴',
      origin: '🌟',
      uplay: '🏰',
      other: '📦'
    };
    return icons[platform] || '📦';
  };

  const getPlatformName = (platform, other) => {
    if (platform === 'other' && other) return other;
    const names = {
      steam: 'Steam',
      epic: 'Epic Games',
      gog: 'GOG',
      xbox: 'Xbox',
      playstation: 'PlayStation',
      nintendo: 'Nintendo',
      origin: 'Origin',
      uplay: 'Uplay',
      other: 'Other'
    };
    return names[platform] || platform;
  };

  const handleClaimCode = async () => {
    if (!isAuthenticated) {
      alert('Please login to claim codes');
      return;
    }

    if (!hasCodesAvailable) {
      alert('No codes available');
      return;
    }

    try {
      setClaiming(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/code-requests/${codeRequest._id}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setClaimedCode(response.data.data.code);
      setShowCodeModal(true);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error claiming code:', error);
      alert(error.response?.data?.message || 'Failed to claim code');
    } finally {
      setClaiming(false);
    }
  };

  const handleVouchClick = () => {
    if (!isAuthenticated) {
      alert('Please login to vouch');
      return;
    }
    setShowVouchModal(true);
  };

  const handleUserClick = (e) => {
    e.stopPropagation();
    navigate(`/profile/${codeRequest.user.username}`);
  };

  const handleMarkAsDone = async () => {
    if (!window.confirm('⚠️ WARNING: Only click this button if you have ALREADY sent the game code to the requester via website DMs.\n\nPeople who click "Mark as Done" without sending the code will face consequences including account suspension.\n\nAre you sure you want to proceed?')) {
      return;
    }

    try {
      setMarkingAsDone(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/code-requests/${codeRequest._id}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Code request marked as completed! The requester has been notified via email.');
      setShowDetailsModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error marking as done:', error);
      alert(error.response?.data?.message || 'Failed to mark as completed');
    } finally {
      setMarkingAsDone(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/code-requests/${codeRequest._id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Code request updated successfully!');
      setShowEditModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating code request:', error);
      alert(error.response?.data?.message || 'Failed to update code request');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this code request? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/code-requests/${codeRequest._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Code request deleted successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting code request:', error);
      alert(error.response?.data?.message || 'Failed to delete code request');
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = () => {
    if (codeRequest.status === 'completed') return { text: '✅ Completed', class: 'completed' };
    if (codeRequest.status === 'closed') return { text: '🔒 Closed', class: 'closed' };
    if (isOffer && !hasCodesAvailable) return { text: '❌ Out of Codes', class: 'out' };
    return { text: '🔥 Active', class: 'active' };
  };

  const status = getStatusBadge();

  return (
    <>
      <div className={`code-request-card ${codeRequest.type}`} onClick={() => setShowDetailsModal(true)}>
        {/* Game Banner */}
        <div
          className="code-request-banner"
          style={{
            backgroundImage: codeRequest.gameBanner
              ? `url(${codeRequest.gameBanner})`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          {/* Type Badge */}
          <div className={`type-badge ${codeRequest.type}`}>
            {isRequest ? '📥 Request' : '📤 Offer'}
          </div>

          {/* Status Badge */}
          <div className={`status-badge ${status.class}`}>
            {status.text}
          </div>
        </div>

        {/* Content */}
        <div className="code-request-content">
          {/* Game Name */}
          <h3 className="code-request-game-name">
            {codeRequest.gameName}
          </h3>

          {/* Platform */}
          <div className="code-request-platform">
            <span className="platform-icon">
              {getPlatformIcon(codeRequest.platform)}
            </span>
            <span className="platform-name">
              {getPlatformName(codeRequest.platform, codeRequest.platformOther)}
            </span>
          </div>

          {/* Description */}
          {codeRequest.description && (
            <p className="code-request-description">
              {codeRequest.description}
            </p>
          )}

          {/* User Info */}
          <div className="code-request-user" onClick={handleUserClick}>
            <div className="user-avatar">
              {codeRequest.user.profile?.avatar?.url && !imageError ? (
                <img
                  src={codeRequest.user.profile.avatar.url}
                  alt={codeRequest.user.username}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="avatar-placeholder">
                  {codeRequest.user.username.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="user-info">
              <p className="user-username">{codeRequest.user.username}</p>
              <p className="user-vouches">
                ⭐ {codeRequest.user.profile?.vouchCount || 0} vouches
              </p>
            </div>
          </div>

          {/* Stats */}
          {isOffer && (
            <div className="code-request-stats">
              <div className="stat">
                <span className="stat-label">Available:</span>
                <span className="stat-value">
                  {codeRequest.totalCodes - codeRequest.claimedCodes} / {codeRequest.totalCodes}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="code-request-actions">
            {/* Owner Actions - Edit and Delete */}
            {isOwner && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowEditModal(true); }}
                  className="action-btn secondary"
                  style={{ background: 'var(--accent-blue)' }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                  disabled={deleting}
                  className="action-btn"
                  style={{ background: 'var(--error-text)' }}
                >
                  {deleting ? 'Deleting...' : '🗑️ Delete'}
                </button>
              </>
            )}

            {/* Game Link */}
            {codeRequest.gameUrl && (
              <a
                href={codeRequest.gameUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="action-btn secondary"
                onClick={(e) => e.stopPropagation()}
              >
                🔗 Game Page
              </a>
            )}

            {/* Offer Actions */}
            {isOffer && !isOwner && (
              <>
                {hasCodesAvailable && codeRequest.status === 'active' ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleClaimCode(); }}
                    disabled={claiming}
                    className="action-btn primary"
                  >
                    {claiming ? 'Claiming...' : '🎁 Claim Code'}
                  </button>
                ) : (
                  <button className="action-btn disabled" disabled>
                    No Codes Available
                  </button>
                )}
              </>
            )}

            {/* Owner Actions for Offers */}
            {isOffer && isOwner && codeRequest.claimedCodes > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowClaimersModal(true); }}
                className="action-btn secondary"
              >
                👥 View Claimers ({codeRequest.claimedCodes})
              </button>
            )}

            {/* Request Actions */}
            {isRequest && !isOwner && isAuthenticated && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/messages?userId=${codeRequest.user._id}`); }}
                className="action-btn primary"
              >
                💬 Contact
              </button>
            )}

            {/* Vouch Button - Only for offers */}
            {isOffer && !isOwner && isAuthenticated && (
              <button
                onClick={(e) => { e.stopPropagation(); handleVouchClick(); }}
                className="action-btn vouch-btn"
              >
                ⭐ Vouch
              </button>
            )}
          </div>

          {/* Date */}
          <p className="code-request-date">
            Posted on {formatDate(codeRequest.createdAt)}
          </p>
        </div>
      </div>

      {/* Code Display Modal */}
      {showCodeModal && claimedCode && (
        <div className="modal-overlay" onClick={() => setShowCodeModal(false)}>
          <div className="modal-content code-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎉 Code Claimed Successfully!</h2>
            <div className="claimed-code-display">
              <p className="claimed-game-name">{codeRequest.gameName}</p>
              <div className="code-box">
                <code>{claimedCode}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(claimedCode);
                    alert('Code copied to clipboard!');
                  }}
                  className="copy-btn"
                >
                  📋 Copy
                </button>
              </div>
              <p className="code-instructions">
                Please copy this code and redeem it on{' '}
                {getPlatformName(codeRequest.platform, codeRequest.platformOther)}.
              </p>
              <p className="vouch-reminder">
                Don't forget to vouch for {codeRequest.user.username} after redeeming!
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={handleVouchClick}
                className="action-btn primary"
              >
                ⭐ Vouch Now
              </button>
              <button
                onClick={() => setShowCodeModal(false)}
                className="action-btn secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vouch Modal */}
      {showVouchModal && (
        <VouchModal
          toUserId={codeRequest.user._id}
          toUsername={codeRequest.user.username}
          codeRequestId={codeRequest._id}
          gameName={codeRequest.gameName}
          onClose={() => setShowVouchModal(false)}
          onSuccess={() => {
            setShowVouchModal(false);
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {/* View Claimers Modal */}
      {showClaimersModal && (
        <ViewClaimersModal
          codeRequestId={codeRequest._id}
          onClose={() => setShowClaimersModal(false)}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <h2>✏️ Edit Code {isRequest ? 'Request' : 'Offer'}</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Game Name</label>
                <input
                  type="text"
                  value={codeRequest.gameName}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  className="input-field"
                />
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                  Game name cannot be changed
                </p>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  placeholder="Update description (optional)"
                  rows="4"
                  className="input-field"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label>Game URL</label>
                <input
                  type="url"
                  value={editFormData.gameUrl}
                  onChange={(e) => setEditFormData({ ...editFormData, gameUrl: e.target.value })}
                  placeholder="https://store.steampowered.com/app/..."
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="input-field"
                >
                  <option value="active">🔥 Active</option>
                  <option value="closed">🔒 Closed</option>
                </select>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                  Setting to "Closed" will prevent new claims/offers
                </p>
              </div>

              {isOffer && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.emailNotificationsEnabled}
                      onChange={(e) => setEditFormData({ ...editFormData, emailNotificationsEnabled: e.target.checked })}
                    />
                    <span className="checkmark"></span>
                    Send email notifications to claimants
                  </label>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                    When someone claims your code, they will receive an email with the code details in addition to the website message.
                  </p>
                </div>
              )}

              <div className="modal-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={updating}
                  className="action-btn primary"
                  style={{ flex: 1 }}
                >
                  {updating ? 'Saving...' : '💾 Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="action-btn secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
            {/* Game Banner */}
            <div
              className="details-banner"
              style={{
                backgroundImage: codeRequest.gameBanner
                  ? `url(${codeRequest.gameBanner})`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              {/* Type Badge */}
              <div className={`type-badge ${codeRequest.type}`}>
                {isRequest ? '📥 Request' : '📤 Offer'}
              </div>

              {/* Status Badge */}
              <div className={`status-badge ${status.class}`}>
                {status.text}
              </div>
            </div>

            <h2>{codeRequest.gameName}</h2>
            
            <div className="details-content">
              {/* Platform */}
              <div className="details-platform">
                <span className="platform-icon">
                  {getPlatformIcon(codeRequest.platform)}
                </span>
                <span className="platform-name">
                  {getPlatformName(codeRequest.platform, codeRequest.platformOther)}
                </span>
              </div>

              {/* Full Description */}
              {codeRequest.description && (
                <div className="details-description">
                  <h3>Description</h3>
                  <p>{codeRequest.description}</p>
                </div>
              )}

              {/* User Info */}
              <div className="details-user" onClick={(e) => { e.stopPropagation(); handleUserClick(e); }}>
                <h3>Posted by</h3>
                <div className="user-info-section">
                  <div className="user-avatar">
                    {codeRequest.user.profile?.avatar?.url && !imageError ? (
                      <img
                        src={codeRequest.user.profile.avatar.url}
                        alt={codeRequest.user.username}
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {codeRequest.user.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="user-details">
                    <p className="user-username">{codeRequest.user.username}</p>
                    <p className="user-vouches">
                      ⭐ {codeRequest.user.profile?.vouchCount || 0} vouches
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats for Offers */}
              {isOffer && (
                <div className="details-stats">
                  <h3>Statistics</h3>
                  <div className="stat-grid">
                    <div className="stat">
                      <span className="stat-label">Available Codes:</span>
                      <span className="stat-value">
                        {codeRequest.totalCodes - codeRequest.claimedCodes} / {codeRequest.totalCodes}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Date */}
              <div className="details-date">
                <p>Posted on {formatDate(codeRequest.createdAt)}</p>
              </div>
            </div>

            <div className="modal-actions">
              {/* Warning message for Mark as Done - shown above all buttons */}
              {isRequest && !isOwner && isAuthenticated && codeRequest.status === 'active' && (
                <div className="mark-done-warning">
                  <span className="warning-icon">⚠️</span>
                  <div className="warning-content">
                    <h4>Important Warning</h4>
                    <p>Only click "Mark as Done" if you have ALREADY sent the game code to the requester via website DMs. People who click this button without sending the code will face consequences including account suspension.</p>
                  </div>
                </div>
              )}

              {/* All buttons wrapped in a row container */}
              <div className="modal-buttons-row">
                {/* Game Link */}
                {codeRequest.gameUrl && (
                  <a
                    href={codeRequest.gameUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-btn secondary"
                  >
                    🔗 Game Page
                  </a>
                )}

                {/* Owner Actions */}
                {isOwner && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowEditModal(true); setShowDetailsModal(false); }}
                      className="action-btn secondary"
                      style={{ background: 'var(--accent-blue)' }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(); setShowDetailsModal(false); }}
                      disabled={deleting}
                      className="action-btn"
                      style={{ background: 'var(--error-text)' }}
                    >
                      {deleting ? 'Deleting...' : '🗑️ Delete'}
                    </button>
                  </>
                )}

                {/* Offer Actions */}
                {isOffer && !isOwner && (
                  <>
                    {hasCodesAvailable && codeRequest.status === 'active' ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleClaimCode(); setShowDetailsModal(false); }}
                        disabled={claiming}
                        className="action-btn primary"
                      >
                        {claiming ? 'Claiming...' : '🎁 Claim Code'}
                      </button>
                    ) : (
                      <button className="action-btn disabled" disabled>
                        No Codes Available
                      </button>
                    )}
                  </>
                )}

                {/* Owner Actions for Offers */}
                {isOffer && isOwner && codeRequest.claimedCodes > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowClaimersModal(true); setShowDetailsModal(false); }}
                    className="action-btn secondary"
                  >
                    👥 View Claimers ({codeRequest.claimedCodes})
                  </button>
                )}

                {/* Request Actions */}
                {isRequest && !isOwner && isAuthenticated && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/messages?userId=${codeRequest.user._id}`); }}
                    className="action-btn primary"
                  >
                    💬 Contact
                  </button>
                )}

                {/* Mark as Done Button - For requests, not owned by current user */}
                {isRequest && !isOwner && isAuthenticated && codeRequest.status === 'active' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMarkAsDone(); }}
                    disabled={markingAsDone}
                    className="action-btn success"
                    style={{ background: 'var(--success-color, #4CAF50)' }}
                  >
                    {markingAsDone ? 'Marking...' : '✅ Mark as Done'}
                  </button>
                )}

                {/* Vouch Button */}
                {isOffer && !isOwner && isAuthenticated && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleVouchClick(); setShowDetailsModal(false); }}
                    className="action-btn vouch-btn"
                  >
                    ⭐ Vouch
                  </button>
                )}

                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="action-btn secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CodeRequestCard;
